import { definedMap } from "@glideapps/ts-necessities";
import chroma from "chroma-js";
import type { IconImage, SerializedApp } from "common/components/SerializedApp";
import { getSerializedAppSchema } from "common/components/SerializedApp";
import type { AppFacilities, DataStore } from "common/components/types";
import type { Database } from "common/Database";
import type { EminenceFlags } from "common/Database/eminence";
import type { AllQuotaValuesWithLimitsForKinds } from "common/Database/quotas";
import { WireModalSize } from "common/generator/components/parsed-path";
import { isDefined, nonNull } from "common/Support";
import type { WireNavigationModel } from "common/wire";
import { WireScreenFlag } from "common/wire";
import { useWireBackend, useWireNavigationModel } from "hooks/use-wire-backend";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import { SimpleHeartbeatAndQuotaManager } from "lib/heartbeat-and-quota";
import React from "react";
import { createPortal } from "react-dom";
import { Toaster } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import { isPlayer } from "routes";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import type { DesktopNavBarProps } from "webapp/chrome/smart/desktop-container/private/desktop-nav-bar";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import { DesktopNavBar } from "webapp/chrome/smart/desktop-container/private/desktop-nav-bar";
// eslint-disable-next-line glide/no-out-of-bounds-view-imports
import { DesktopSideBar } from "webapp/chrome/smart/desktop-container/private/desktop-side-bar";
import type { WireBackend } from "webapp/hydrated-ui/types";
import { styled } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { ResponsiveProvider } from "./responsive-provider";
import { WireApp } from "./wire-app";
import { makeSingleActionSpreadProps } from "./wire-lib";

const MadeWithGlide = () => {
    return (
        <svg viewBox="0 0 95 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 10.6L11.6 4V7.24C11.6 9.09568 10.0957 10.6 8.24 10.6H5Z" fill="currentColor" />
            <path d="M10.4 12.76C10.4 10.9043 11.9043 9.4 13.76 9.4H17L10.4 16V12.76Z" fill="currentColor" />
            <g clipPath="url(#clip0)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M76.9533 6.77889C76.7816 6.91749 76.6661 7.11131 76.6275 7.32556C76.5605 7.77343 76.876 8.11891 77.3352 8.11891C77.8037 8.11891 78.196 7.81089 78.2629 7.38133C78.3392 6.92388 78.0237 6.56924 77.5645 6.56924C77.3418 6.56595 77.1251 6.64028 76.9533 6.77889ZM72.9844 10.5338C72.9955 10.3246 72.9922 10.1148 72.9746 9.90602H69.4708L68.8773 11.0976H71.4864C71.2878 11.7855 70.5435 12.2211 69.6511 12.2211C68.3012 12.2211 67.4088 11.3398 67.4088 10.0903C67.4088 8.86956 68.3217 7.97863 69.5417 7.97863C70.4646 7.97863 71.0103 8.44261 71.2887 8.98589H72.8059C72.4387 7.61049 71.2584 6.68034 69.5417 6.68034C67.498 6.68034 66 8.13329 66 10.0903C66 12.0661 67.4784 13.4998 69.6511 13.4963C71.6457 13.4963 72.905 12.1127 72.9844 10.5338ZM74.2937 6.78708L73.2223 13.3931H74.5917L75.6627 6.78708H74.2937ZM75.7912 13.3931L76.5253 8.84037H77.8943L77.1603 13.3931H75.7912ZM84.005 6.78708H82.636L82.2187 9.33442C81.9711 8.9763 81.5249 8.73407 80.8801 8.73407C79.7886 8.73407 78.6284 9.45074 78.3896 10.9233C78.1018 12.7052 79.2924 13.4998 80.2152 13.4998C80.8305 13.4998 81.3365 13.2572 81.6935 12.8215L81.9889 13.3931H83.5088L83.167 12.178L84.005 6.78708ZM81.009 9.89643C81.5347 9.89643 82.1001 10.3613 81.9711 11.1751C81.8323 12.0469 81.1576 12.3375 80.681 12.3375C80.0563 12.3375 79.6003 11.8142 79.719 11.0684C79.8283 10.3613 80.3539 9.89643 81.009 9.89643ZM88.9261 11.5528H85.4236C85.5726 12.134 85.9894 12.4242 86.5646 12.4242C87.0907 12.4242 87.3678 12.2983 87.557 12.0173H88.7971C88.4995 13.0729 87.5173 13.4985 86.5945 13.4985C85.1161 13.4985 84.1541 12.4812 84.1541 11.1154C84.1541 9.75963 85.146 8.73276 86.5548 8.73276C87.9635 8.73276 88.9363 9.76094 88.9363 11.1167V11.1168C88.9363 11.301 88.9363 11.3686 88.9261 11.5528ZM85.4138 10.6615H87.6869C87.5976 10.1674 87.1211 9.81888 86.5557 9.81888C85.9595 9.81888 85.545 10.1578 85.4138 10.6615Z"
                    fill="currentColor"
                />
            </g>
            <path
                d="M22.629 13.5H21.621V7.2H22.836L24.843 11.988L26.85 7.2H28.065V13.5H27.057V9.171L25.257 13.5H24.438L22.629 9.171V13.5ZM30.9856 8.892C31.5736 8.892 32.0446 9.051 32.3986 9.369C32.7526 9.681 32.9296 10.116 32.9296 10.674V13.5H31.9666V12.807C31.8346 13.035 31.6396 13.221 31.3816 13.365C31.1236 13.503 30.8446 13.572 30.5446 13.572C30.2506 13.572 29.9866 13.515 29.7526 13.401C29.5246 13.287 29.3446 13.128 29.2126 12.924C29.0866 12.72 29.0236 12.489 29.0236 12.231C29.0236 11.787 29.1946 11.442 29.5366 11.196C29.8846 10.95 30.4096 10.791 31.1116 10.719L31.9666 10.629V10.566C31.9666 10.29 31.8766 10.071 31.6966 9.909C31.5226 9.747 31.2856 9.666 30.9856 9.666C30.7096 9.666 30.4816 9.744 30.3016 9.9C30.1216 10.05 30.0166 10.26 29.9866 10.53L29.1046 10.368C29.1766 9.924 29.3866 9.567 29.7346 9.297C30.0826 9.027 30.4996 8.892 30.9856 8.892ZM30.7426 12.816C31.1206 12.816 31.4176 12.699 31.6336 12.465C31.8496 12.231 31.9606 11.925 31.9666 11.547V11.358L31.1026 11.457C30.7366 11.499 30.4576 11.586 30.2656 11.718C30.0796 11.844 29.9866 12.006 29.9866 12.204C29.9866 12.384 30.0556 12.531 30.1936 12.645C30.3316 12.759 30.5146 12.816 30.7426 12.816ZM36.0088 13.572C35.5588 13.572 35.1688 13.473 34.8388 13.275C34.5148 13.077 34.2658 12.801 34.0918 12.447C33.9238 12.093 33.8398 11.688 33.8398 11.232C33.8398 10.776 33.9238 10.371 34.0918 10.017C34.2658 9.663 34.5148 9.387 34.8388 9.189C35.1628 8.991 35.5468 8.892 35.9908 8.892C36.2788 8.892 36.5458 8.952 36.7918 9.072C37.0438 9.186 37.2388 9.339 37.3768 9.531V7.128H38.3578V13.5H37.3768V12.924C37.2688 13.116 37.0888 13.272 36.8368 13.392C36.5848 13.512 36.3088 13.572 36.0088 13.572ZM36.1348 12.717C36.5308 12.717 36.8488 12.582 37.0888 12.312C37.3288 12.036 37.4488 11.676 37.4488 11.232C37.4488 10.782 37.3288 10.422 37.0888 10.152C36.8488 9.882 36.5308 9.747 36.1348 9.747C35.7388 9.747 35.4208 9.882 35.1808 10.152C34.9408 10.422 34.8208 10.782 34.8208 11.232C34.8208 11.676 34.9408 12.036 35.1808 12.312C35.4208 12.582 35.7388 12.717 36.1348 12.717ZM43.7175 11.061C43.7175 11.223 43.7115 11.358 43.6995 11.466H40.2345C40.2765 11.856 40.4145 12.165 40.6485 12.393C40.8885 12.621 41.1885 12.735 41.5485 12.735C41.8485 12.735 42.0975 12.669 42.2955 12.537C42.4935 12.399 42.6225 12.207 42.6825 11.961L43.6095 12.195C43.4895 12.633 43.2465 12.972 42.8805 13.212C42.5145 13.452 42.0645 13.572 41.5305 13.572C41.0685 13.572 40.6665 13.464 40.3245 13.248C39.9825 13.032 39.7215 12.747 39.5415 12.393C39.3615 12.033 39.2715 11.646 39.2715 11.232C39.2715 10.812 39.3585 10.425 39.5325 10.071C39.7065 9.717 39.9585 9.432 40.2885 9.216C40.6245 9 41.0235 8.892 41.4855 8.892C41.9535 8.892 42.3555 8.994 42.6915 9.198C43.0275 9.396 43.2825 9.663 43.4565 9.999C43.6305 10.329 43.7175 10.683 43.7175 11.061ZM42.6735 10.719C42.6435 10.401 42.5235 10.146 42.3135 9.954C42.1095 9.762 41.8335 9.666 41.4855 9.666C41.1735 9.666 40.9125 9.759 40.7025 9.945C40.4925 10.125 40.3515 10.383 40.2795 10.719H42.6735ZM48.7358 13.5H47.8358L46.4318 8.964H47.4308L48.2858 12.132L49.2668 8.964H50.1758L51.1478 12.132L52.0028 8.964H53.0018L51.6068 13.5H50.6978L49.7168 10.341L48.7358 13.5ZM54.7777 13.5H53.7967V8.964H54.7777V13.5ZM54.2827 8.208C54.1027 8.208 53.9527 8.148 53.8327 8.028C53.7127 7.902 53.6527 7.752 53.6527 7.578C53.6527 7.398 53.7127 7.248 53.8327 7.128C53.9527 7.002 54.1027 6.939 54.2827 6.939C54.4687 6.939 54.6217 7.002 54.7417 7.128C54.8677 7.248 54.9307 7.398 54.9307 7.578C54.9307 7.752 54.8677 7.902 54.7417 8.028C54.6217 8.148 54.4687 8.208 54.2827 8.208ZM58.5152 13.464C58.2992 13.536 58.0592 13.572 57.7952 13.572C57.3452 13.572 56.9822 13.446 56.7062 13.194C56.4362 12.942 56.3012 12.567 56.3012 12.069V9.801H55.5092V8.964H56.3012V7.614H57.2822V8.964H58.4522V9.801H57.2822V12.015C57.2822 12.237 57.3362 12.408 57.4442 12.528C57.5582 12.642 57.7112 12.699 57.9032 12.699C58.1192 12.699 58.3022 12.663 58.4522 12.591L58.5152 13.464ZM59.5008 13.5V7.128H60.4818V9.747C60.6378 9.465 60.8418 9.252 61.0938 9.108C61.3518 8.964 61.6428 8.892 61.9668 8.892C62.4828 8.892 62.8938 9.042 63.1998 9.342C63.5118 9.636 63.6678 10.029 63.6678 10.521V13.5H62.6868V10.746C62.6868 10.446 62.5968 10.209 62.4168 10.035C62.2428 9.861 62.0028 9.774 61.6968 9.774C61.3188 9.774 61.0218 9.93 60.8058 10.242C60.5898 10.554 60.4818 10.986 60.4818 11.538V13.5H59.5008Z"
                fill="currentColor"
            />
            <defs>
                <clipPath id="clip0">
                    <rect width="23" height="7" fill="white" transform="translate(66 6.5)" />
                </clipPath>
            </defs>
        </svg>
    );
};

interface PlayerProps {
    readonly appID: string;
    readonly database: Database;
    readonly appFacilities: AppFacilities;
    readonly dataStore: DataStore;
    readonly serializedApp: SerializedApp;
    readonly eminenceFlags: EminenceFlags;
}

const PlayerDesktopWireApp: React.VFC<PlayerProps> = p => {
    const { dataStore, appID, serializedApp, appFacilities, database, eminenceFlags } = p;
    const [schema, builderActions] = React.useMemo(
        () => [getSerializedAppSchema(serializedApp), new Map(Object.entries(serializedApp.builderActions ?? {}))],
        [serializedApp]
    );
    const history = useHistory();
    const location = useLocation();
    const theme = useWireAppTheme();
    const deepLink = location.pathname.split("/dl/", 2)[1] ?? "";

    const { backend, urlStack } = useWireBackend(
        appID,
        serializedApp,
        builderActions,
        schema,
        // we only need to pass an authenticator when we're in the builder
        undefined,
        dataStore,
        false,
        deepLink
    );
    const navModel = useWireNavigationModel(backend, urlStack);

    let current = navModel === undefined ? undefined : `/dl/${navModel.urlPath}`;

    if (window.location.pathname.startsWith("/play/")) {
        current = `/play/${appID}${current}`;
    }

    React.useEffect(() => {
        if (current !== undefined && window.location.pathname.endsWith(current) === false) {
            history.push(current);
        }
    }, [current, history]);

    React.useEffect(() => {
        backend?.urlPathChanged(deepLink);
    }, [backend, deepLink]);

    React.useEffect(() => {
        class Manager extends SimpleHeartbeatAndQuotaManager {
            protected quotasDidUpdate(
                quotas: AllQuotaValuesWithLimitsForKinds,
                prior: AllQuotaValuesWithLimitsForKinds
            ): void {
                if (quotas["rows-used"]?.limit !== prior["rows-used"]?.limit) {
                    dataStore.resetFromUpstream();
                }
            }
        }

        const heartbeatManager = new Manager(appID, appFacilities, database, eminenceFlags);
        heartbeatManager.isMounted(false);
        backend?.setHeartbeatManager(heartbeatManager);
        return () => heartbeatManager.isUnmounted(false);
    }, [appID, appFacilities, database, eminenceFlags, dataStore, backend]);

    if (backend === undefined || navModel === undefined) return null;

    return (
        <TailwindThemeProvider theme={theme} setPortal={true}>
            <InnerWireApp backend={backend} iconImage={serializedApp.iconImage} navModel={navModel} />
        </TailwindThemeProvider>
    );
};

interface InnerProps {
    readonly navModel: WireNavigationModel;
    readonly backend: WireBackend;
    readonly iconImage?: IconImage;
}

const InnerStyle = styled.div`
    .accent-bg {
        background: linear-gradient(
            90deg,
            ${p => chroma(p.theme.primaryAccentColor).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.6).alpha(1).css()} 100%
        );
    }

    &.no-sidebar {
        flex-direction: column !important;
        .topbar-root {
            display: flex !important;
        }

        .sidebar-root {
            display: none !important;
        }
    }
`;

const ModalStyle = styled.div`
    .accent-bg {
        background: linear-gradient(
            90deg,
            ${p => chroma(p.theme.primaryAccentColor).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.5).alpha(1).css()} 100%
        );
    }

    &.small {
        justify-content: center;
        padding: 0 12px;

        .inner-container {
            width: 100%;
            max-width: 640px;
            max-height: 80%;

            .modal-window {
                overflow-y: auto;
                ${tw`flex-shrink`}
            }
        }
    }

    &.large {
        overflow-y: auto;

        .inner-container {
            max-width: 80%;
            width: 1280px;
            padding-top: 72px;
            flex-shrink: 0;

            .modal-rounder {
                ${tw`rounded-2xl`}
                margin-bottom: 72px;
            }
        }
    }
`;

export const InnerWireApp: React.VFC<InnerProps> = p => {
    const { navModel, backend } = p;

    const currentScreen = navModel.screen;
    const currentSpecial = currentScreen.specialButtons.filter(isDefined) ?? [];
    const modalScreen = navModel.modal;
    const modalSpecial = modalScreen?.specialButtons.filter(isDefined) ?? [];

    const modalRef = React.useRef<HTMLDivElement>(null);
    const pageRef = React.useRef<HTMLDivElement>(null);

    let modalSpecialGroup: React.ReactNode;
    if (modalSpecial.length > 0) {
        modalSpecialGroup = (
            <div
                tw="sticky self-stretch bottom[-0.5px] px-8 py-6 flex bg-bg-front box-shadow[0 -1px 0 rgba(62, 65, 86,
                    0.12)]">
                {modalSpecial.map((b, i) => {
                    return (
                        <WireButton
                            appearance={b.appearance}
                            {...makeSingleActionSpreadProps(b.action, backend)}
                            key={i}
                            tw="mr-4">
                            {b.title ?? "Err"}
                        </WireButton>
                    );
                })}
            </div>
        );
    }

    let mainSpecialGroup: React.ReactNode;
    if (currentSpecial.length > 0) {
        mainSpecialGroup = (
            <>
                <div tw="flex-grow" />
                <div
                    tw="sticky self-stretch bottom[-0.5px] px-8 py-6 bg-bg-front box-shadow[0 -1px 0 rgba(62, 65, 86,
                        0.12)]">
                    {currentSpecial.map((b, i) => {
                        return (
                            <WireButton
                                {...makeSingleActionSpreadProps(b.action, backend)}
                                appearance={b.appearance}
                                key={i}
                                tw="mr-4">
                                {b.title ?? "Err"}
                            </WireButton>
                        );
                    })}
                </div>
            </>
        );
    }

    React.useEffect(() => {
        pageRef.current?.scrollTo({
            top: 0,
        });
    }, [currentScreen.key]);

    React.useEffect(() => {
        modalRef.current?.scrollTo({
            top: 0,
        });
    }, [modalScreen?.key]);

    const theme = useWireAppTheme();
    let colorTheme = theme.bgFront;

    if (modalScreen?.flags.includes(WireScreenFlag.IsSignIn) === true && theme.signInColorTheme !== "black") {
        colorTheme = theme.n900;
    }

    const sidebarProps: DesktopNavBarProps = {
        title: navModel.appTitle,
        logo: theme.signInLogo,
        tabs: navModel.tabs,
        isUserProfileActive: navModel.userProfileButton?.userProfileScreenIsOpen === true,
        signedInUserEmail: navModel.userProfileButton?.signedInUserEmail,
        signedInUserName: navModel.userProfileButton?.signedInUserName,
        signedInUserImage: navModel.userProfileButton?.signedInUserImage,
        onUserButtonPressed: definedMap(
            navModel.userProfileButton?.userProfileAction?.token,
            x => () => backend.runAction(x, false)
        ),
        selected: navModel.tabs.find(x => x.isActive),
        onSelectionChange: x => backend.runAction(x.action.token, false),
        onSignInPressed: definedMap(navModel.signInAction?.token, x => () => backend.runAction(x, false)),
        onSignUpPressed: definedMap(navModel.signUpAction?.token, x => () => backend.runAction(x, false)),
        onTitlePressed: definedMap(navModel.appTitleAction?.token, x => () => backend.runAction(x, false)),
        onSignOutPressed: definedMap(navModel.signOutAction?.token, x => () => backend.runAction(x, false)),
    };

    return (
        <ResponsiveProvider id="page-root" prefix="page" tw="absolute inset-0 text-base min-width[320px]">
            {navModel.blockingMessage !== undefined && (
                <div
                    tw="absolute inset-0 p-10 background-color[rgba(17, 24, 39, 0.33)] z-index[100000] text-text-dark
                        flex flex-col justify-center items-center">
                    <div tw="w-full max-width[480px] rounded-xl shadow-2xl-dark">
                        <div tw="rounded-xl overflow-hidden background-color[#374664E0] backdrop-blur backdrop-filter">
                            <div tw="pt-10 pb-8 px-10 bg-bg-front">
                                <h2 tw="font-extrabold text-xl mb-2">{navModel.blockingMessage.title}</h2>
                                <div tw="text-sm whitespace-pre-wrap break-words mb-2">
                                    {navModel.blockingMessage.message}
                                </div>
                                {navModel.blockingMessage.link !== undefined && (
                                    <a
                                        tw="text-sm text-b400"
                                        href={navModel.blockingMessage.link.url}
                                        target="_blank"
                                        rel="noreferrer noopener">
                                        {navModel.blockingMessage.link.text}
                                    </a>
                                )}
                            </div>
                            <a
                                href="https://glideapps.com"
                                target="_blank"
                                rel="noreferrer noopener"
                                tw="h-16 flex items-center px-8 text-white all-child:h-8">
                                <MadeWithGlide />
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {isPlayer() &&
                createPortal(
                    <div id="toaster-hack">
                        <Toaster position={"bottom-center"} />
                    </div>,
                    nonNull(document.getElementById("portal"))
                )}
            <a
                href="https://glideapps.com"
                target="_blank"
                rel="noreferrer"
                tw="z-50 absolute color[#374664] right-2.5 bottom-2.5 bg-white transform shadow-lg rounded-md
                    transition hover:(scale-105 shadow-2xl-xdark) all-child:h-6">
                <MadeWithGlide />
            </a>
            <InnerStyle
                ref={pageRef}
                className={theme.showDesktopSideBar ? "" : "no-sidebar"}
                tw="absolute inset-0 bg-bg-back pointer-events-auto flex-col page-lg:flex-row flex overflow-y-auto">
                <ResponsiveProvider
                    id="nav-root"
                    className="topbar-root"
                    tw="z-10 flex page-lg:hidden flex-col items-center sticky top-0">
                    <DesktopNavBar tw="w-full" {...sidebarProps} />
                </ResponsiveProvider>
                <DesktopSideBar className="sidebar-root" tw="sticky top-0 hidden page-lg:flex" {...sidebarProps} />
                <main tw="flex flex-grow min-h-0">
                    <ResponsiveProvider id="main-root" tw="relative flex flex-col items-center flex-1 min-w-0">
                        <div tw="relative all-child:flex-shrink-0 flex flex-col w-full">
                            <WireApp currentScreen={currentScreen} backend={backend} />
                        </div>
                        {mainSpecialGroup}
                    </ResponsiveProvider>
                </main>
            </InnerStyle>
            {modalScreen !== undefined && (
                <ModalStyle
                    ref={modalRef}
                    tw="absolute inset-0 h-full w-full flex flex-col items-center background-color[rgba(17, 24, 39,
                        0.33)] z-20"
                    className={modalScreen.size}>
                    <div className="inner-container" tw="relative flex flex-col">
                        {modalScreen.size === WireModalSize.Large && (
                            <div tw="w-full sticky top[72px]">
                                <button
                                    tw="flex justify-center items-center absolute -top-10 -right-11 p-2 self-end
                                        transform background-color[rgba(17, 24, 39, 0.5)] rounded-full z-10 text-white"
                                    onClick={() =>
                                        modalScreen?.closeAction &&
                                        backend.runAction(modalScreen.closeAction?.token, false)
                                    }>
                                    <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                                </button>
                            </div>
                        )}
                        <div className="modal-rounder" tw="rounded-xl overflow-hidden flex flex-col">
                            <div
                                className="modal-window"
                                style={{ backgroundColor: colorTheme }}
                                tw="w-full shadow-2xl-dark">
                                <ResponsiveProvider id="modal-root" tw="w-full">
                                    <div role="dialog" tw="all-child:flex-shrink-0 flex flex-col relative">
                                        {modalScreen.size === WireModalSize.Small && (
                                            <div
                                                tw="h-16 bg-bg-front sticky top-0 shadow-sm flex items-center
                                                    justify-between pl-8 pr-3 z-10">
                                                <div tw="text-2xl font-extrabold text-text-dark">
                                                    {modalScreen.title}
                                                </div>
                                                <button
                                                    tw="flex justify-center items-center h-10 w-10 text-icon-base
                                                        rounded-lg transition hover:(bg-bg-back text-icon-dark)"
                                                    onClick={() =>
                                                        modalScreen?.closeAction &&
                                                        backend.runAction(modalScreen.closeAction?.token, false)
                                                    }>
                                                    <GlideIcon kind="stroke" icon="st-close" iconSize={24} />
                                                </button>
                                            </div>
                                        )}
                                        <div tw="px-2 all-child:flex-shrink-0 flex flex-col relative">
                                            <WireApp currentScreen={modalScreen} backend={backend} />
                                        </div>
                                        {modalSpecialGroup}
                                    </div>
                                </ResponsiveProvider>
                            </div>
                        </div>
                        <div tw="h-px" />
                    </div>
                </ModalStyle>
            )}
        </ResponsiveProvider>
    );
};

export default PlayerDesktopWireApp;
